import React from "react";
import { AlertTriangle, RotateCcw } from "lucide-react";

/**
 * React ErrorBoundary in Florefy-stijl. Vangt onverwachte client-side errors
 * (witte schermen) op zodat de gebruiker een nette herstelmelding krijgt.
 *
 * Beperkingen van React ErrorBoundary:
 *  - vangt GEEN async errors (Promise rejections) — daarvoor hebben we de
 *    axios-interceptor + sonner-toast in `lib/api.js`.
 *  - vangt GEEN errors in event handlers — die regel je per pagina af.
 */
export default class ErrorBoundary extends React.Component {
    state = { error: null, info: null };

    static getDerivedStateFromError(error) {
        return { error };
    }

    componentDidCatch(error, info) {
        // eslint-disable-next-line no-console
        console.error("[ErrorBoundary]", error, info);
        this.setState({ info });
    }

    handleReset = () => {
        this.setState({ error: null, info: null });
    };

    handleReload = () => {
        window.location.reload();
    };

    render() {
        if (!this.state.error) return this.props.children;
        return (
            <div
                className="min-h-screen flex items-center justify-center bg-ink-50 px-6"
                data-testid="error-boundary"
            >
                <div className="max-w-lg w-full bg-white border border-border rounded-sm p-10 shadow-sm">
                    <div className="flex items-center gap-3 text-florefy-900 mb-4">
                        <AlertTriangle className="h-6 w-6" />
                        <div className="font-mono text-[10px] uppercase tracking-widest text-ink-500">
                            Onverwachte fout
                        </div>
                    </div>
                    <h1 className="text-2xl font-semibold text-ink-900 mb-2">
                        Er ging iets mis
                    </h1>
                    <p className="text-ink-600 mb-6">
                        Sorry, deze pagina kon niet getoond worden. Probeer het
                        opnieuw of ga terug naar het dashboard. Je werk is bewaard.
                    </p>
                    {process.env.NODE_ENV !== "production" && (
                        <pre
                            className="bg-ink-50 border border-border rounded-sm p-3 text-[11px] text-ink-700 mb-6 overflow-auto max-h-40"
                            data-testid="error-boundary-detail"
                        >
                            {String(this.state.error?.stack || this.state.error)}
                        </pre>
                    )}
                    <div className="flex gap-3">
                        <button
                            type="button"
                            onClick={this.handleReload}
                            className="flex-1 px-4 py-2 bg-florefy-900 text-white font-mono text-sm uppercase tracking-widest hover:bg-florefy-700 transition-colors rounded-sm"
                            data-testid="error-boundary-reload"
                        >
                            <RotateCcw className="h-4 w-4 inline mr-2" />
                            Herlaad pagina
                        </button>
                        <a
                            href="/dashboard"
                            className="flex-1 text-center px-4 py-2 border border-border text-ink-700 font-mono text-sm uppercase tracking-widest hover:bg-ink-50 transition-colors rounded-sm"
                            data-testid="error-boundary-home"
                        >
                            Dashboard
                        </a>
                    </div>
                </div>
            </div>
        );
    }
}
